import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Download, Share, PlusSquare, MoreVertical, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { isAndroid, isIOS, isStandalone, useInstallPrompt } from "@/lib/pwa";
import { IOSGuide } from "@/components/InstallAppButton";

const AndroidLogo = () => (
  <svg viewBox="0 0 24 24" className="w-7 h-7" fill="#3DDC84" aria-hidden="true">
    <path d="M17.6 9.48l1.84-3.18a.38.38 0 00-.66-.38l-1.86 3.22a11.4 11.4 0 00-9.84 0L5.22 5.92a.38.38 0 00-.66.38L6.4 9.48A10.8 10.8 0 001 18h22a10.8 10.8 0 00-5.4-8.52zM7 15.25a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5zm10 0a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5z" />
  </svg>
);

const AppleLogo = () => (
  <svg viewBox="0 0 24 24" className="w-7 h-7" fill="#111" aria-hidden="true">
    <path d="M16.37 12.6c-.02-2.3 1.88-3.4 1.96-3.46-1.07-1.56-2.73-1.78-3.32-1.8-1.41-.14-2.76.83-3.47.83-.72 0-1.82-.81-2.99-.79-1.54.02-2.96.9-3.75 2.27-1.6 2.78-.41 6.9 1.15 9.15.76 1.1 1.67 2.34 2.86 2.3 1.15-.05 1.58-.74 2.97-.74 1.38 0 1.78.74 2.99.72 1.24-.02 2.02-1.12 2.77-2.23.87-1.28 1.23-2.52 1.25-2.58-.03-.01-2.4-.92-2.42-3.67zM14.1 5.86c.63-.77 1.06-1.83.94-2.89-.91.04-2.02.61-2.67 1.37-.58.67-1.1 1.76-.96 2.8 1.02.08 2.06-.52 2.69-1.28z" />
  </svg>
);

const Step = ({ icon: Icon, children }) => (
  <li className="flex items-start gap-3 text-sm text-[#2B3036]"><Icon className="w-4 h-4 text-[#E0262C] mt-0.5 flex-shrink-0" /><span>{children}</span></li>
);

const Qr = ({ testid }) => {
  const { tr } = useLang();
  return (
    <div data-testid={testid} className="hidden md:flex items-center gap-3 border-t border-[#e4e7e9] pt-4 mt-auto">
      <div className="bg-white p-1.5 border border-[#e4e7e9]"><QRCodeSVG value={window.location.origin} size={72} fgColor="#2B3036" /></div>
      <p className="text-xs text-[#5b6168]">{tr("Sei al computer? Inquadra il QR code con il telefono per aprire Aurora-ADV e installarla.", "On a computer? Scan the QR code with your phone to open Aurora-ADV and install it.")}</p>
    </div>
  );
};

const Card = ({ testid, logo, title, subtitle, steps, action, qr }) => (
  <div data-testid={testid} className="bg-white border border-[#dfe4e6] p-6 sm:p-8 flex flex-col gap-5 transition-shadow hover:shadow-[0_18px_40px_-24px_rgba(43,48,54,0.35)]">
    <div className="flex items-center gap-4">
      <div className="w-14 h-14 bg-[#f4f6f7] border border-[#e4e7e9] flex items-center justify-center">{logo}</div>
      <div>
        <h3 className="font-display text-2xl leading-tight">{title}</h3>
        <p className="text-sm text-[#5b6168]">{subtitle}</p>
      </div>
    </div>
    <ol className="space-y-2.5">{steps}</ol>
    {action}
    {qr}
  </div>
);

const btn = "inline-flex items-center justify-center gap-2 h-12 px-6 text-sm font-medium transition-opacity hover:opacity-90";

export const AppDownloadCards = () => {
  const { tr } = useLang();
  const { prompt, install } = useInstallPrompt();
  const [guide, setGuide] = useState(false);
  const [done, setDone] = useState(isStandalone());
  const android = async () => {
    if (prompt && (await install())) setDone(true);
    else if (!prompt) document.getElementById("android-steps")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const installed = <div data-testid="app-installed" className="inline-flex items-center gap-2 text-sm text-[#10b981]"><CheckCircle2 className="w-4 h-4" /> {tr("App già installata su questo dispositivo", "App already installed on this device")}</div>;

  return (
    <section data-testid="app-download-section" className="max-w-6xl mx-auto w-full px-6 lg:px-10 py-16">
      <div className="mb-8 max-w-2xl">
        <div className="text-[10px] uppercase tracking-widest font-mono text-[#E0262C] mb-2">{tr("App mobile", "Mobile app")}</div>
        <h2 className="font-display text-3xl sm:text-4xl mb-2">{tr("Scarica l'app Aurora-ADV", "Download the Aurora-ADV app")}</h2>
        <p className="text-sm text-[#5b6168]">{tr("Segui le tue campagne, i nuovi contatti e la chat con il team direttamente dal telefono, con le notifiche in tempo reale. Gratis, senza passare dagli store.", "Follow your campaigns, new contacts and the team chat right from your phone, with real-time notifications. Free, no app store needed.")}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card testid="app-card-android" logo={<AndroidLogo />} title="Android" subtitle={tr("Chrome, Samsung Internet, Edge", "Chrome, Samsung Internet, Edge")}
          steps={<div id="android-steps" className="space-y-2.5">
            <Step icon={MoreVertical}>{tr("Apri questo sito con Chrome e tocca il menu ⋮ in alto a destra", "Open this site in Chrome and tap the ⋮ menu at the top right")}</Step>
            <Step icon={Download}>{tr("Scegli \"Installa app\" (o \"Aggiungi a schermata Home\")", "Choose \"Install app\" (or \"Add to Home screen\")")}</Step>
            <Step icon={CheckCircle2}>{tr("Conferma: l'icona Aurora-ADV appare tra le tue app", "Confirm: the Aurora-ADV icon appears among your apps")}</Step>
          </div>}
          action={done ? installed : (
            <button data-testid="app-android-install" onClick={android} className={`${btn} bg-[#2B3036] text-white`}>
              <Download className="w-4 h-4" /> {prompt ? tr("Installa su Android", "Install on Android") : tr("Come installare su Android", "How to install on Android")}
            </button>
          )}
          qr={!isAndroid() && !isIOS() && <Qr testid="app-qr-android" />} />
        <Card testid="app-card-ios" logo={<AppleLogo />} title="iPhone · iPad" subtitle="Safari"
          steps={<>
            <Step icon={Share}>{tr("Apri questo sito con Safari e tocca il pulsante Condividi", "Open this site in Safari and tap the Share button")}</Step>
            <Step icon={PlusSquare}>{tr("Scegli \"Aggiungi alla schermata Home\"", "Choose \"Add to Home Screen\"")}</Step>
            <Step icon={CheckCircle2}>{tr("Tocca \"Aggiungi\": Aurora-ADV si apre come una vera app", "Tap \"Add\": Aurora-ADV opens like a real app")}</Step>
          </>}
          action={done ? installed : (
            <button data-testid="app-ios-install" onClick={() => setGuide(true)} className={`${btn} aurora-gradient text-white`}>
              <Download className="w-4 h-4" /> {tr("Installa su iPhone", "Install on iPhone")}
            </button>
          )}
          qr={!isAndroid() && !isIOS() && <Qr testid="app-qr-ios" />} />
      </div>
      {guide && <IOSGuide onClose={() => setGuide(false)} />}
    </section>
  );
};
